<script>
  import { fade } from "svelte/transition";
  import Paxbot from "../icons/Paxbot.svelte";
  import Checkmark from "../icons/Checkmark.svelte";
  import WarningTriangle from "../icons/WarningTriangle.svelte";

  import Close from "../icons/Close.svelte";

  // --- InfoBlock props ---
  export let heading = "";
  export let description = "";

  /** @type {1 | 2 | 3 | 4 | 5 | 6} */
  export let headingLevel = 5;

  export let light = false;

  // --- InlineNotice props (active when `type` is provided) ---

  /** @type {"warning" | "hint" | "info" | "loading" | "paxAI" | "neutral" | undefined} */
  export let type = undefined;

  export let message = "";
  export let showClose = true;
  export let showActions = false;
  export let seamless = false;
  export let loading = false;
  export let hideIcon = false;
  /** Size the notice to its text and wrap only when it would exceed the parent width */
  export let fit = false;

  /** @type {{ done: number; total: number } | null} */
  export let progress = null;

  // --- InfoBlock internals ---
  $: safeLevel = Math.max(1, Math.min(6, Number(headingLevel) || 2));

  // --- InlineNotice internals ---
  let visible = true;
  $: isNotice = type !== undefined;
  $: isNeutral = type === "neutral";
  $: isHint = type === "hint";
  $: isInfo = type === "info";
  $: isLoading = type === "loading";
  $: isPaxAI = type === "paxAI" || isLoading;
  $: showProgress = loading || isLoading;
  $: noticeType = type ?? "warning";
  $: displayTitle =
    heading ||
    (isPaxAI ? "Pax AI" : isInfo ? "Information" : isHint ? "Hint" : "Warning");
</script>

{#if isNotice}
  {#if visible}
    <div
      class="warning-notice"
      class:hint="{isHint}"
      class:info="{isInfo}"
      class:pax-ai="{isPaxAI}"
      class:neutral="{isNeutral}"
      class:loading="{showProgress}"
      class:seamless="{seamless}"
      class:fit="{fit}"
      role="{isHint || isInfo || isPaxAI || isLoading ? 'status' : 'alert'}"
    >
      {#if !isNeutral && !isInfo && !hideIcon}
        <span class="attention-icon">
          {#if isPaxAI}
            <Paxbot size="{48}" />
          {:else if isHint}
            <Checkmark size="{24}" />
          {:else}
            <WarningTriangle size="{24}" />
          {/if}
        </span>
      {/if}
      <div class="warning-content">
        <!-- Keep notice animations local so parent route removal is not delayed. -->
        {#if !showProgress}
          <h6
            class="warning-title"
            in:fade|local="{{ duration: 180 }}"
            out:fade|local="{{ duration: 120 }}"
          >
            {displayTitle}
          </h6>
        {/if}
        {#if showProgress}
          <div
            class="notice-progress"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="{progress?.total || 1}"
            aria-valuenow="{progress?.done || 0}"
            in:fade|local="{{ duration: 180 }}"
            out:fade|local="{{ duration: 120 }}"
          >
            <div
              class="notice-progress-value"
              style="{`width: ${
                progress?.total ? (progress.done / progress.total) * 100 : 0
              }%`}"
            ></div>
            <span class="notice-progress-label">
              Creating missing names and types.
              {progress?.done || 0} / {progress?.total || 0}
            </span>
          </div>
        {:else if $$slots.message}
          <div
            class="warning-message"
            in:fade|local="{{ duration: 180 }}"
            out:fade|local="{{ duration: 120 }}"
          >
            <slot name="message" />
          </div>
        {:else}
          <p
            class="warning-message"
            in:fade|local="{{ duration: 180 }}"
            out:fade|local="{{ duration: 120 }}"
          >
            {message}
          </p>
        {/if}
      </div>
      {#if showProgress}
        <div class="notice-cancel">
          <slot name="cancel" />
        </div>
      {/if}
      {#if showActions}
        <div class="notice-actions">
          {#if !showProgress}
            <div out:fade|local="{{ duration: 120 }}">
              <slot name="actions" />
            </div>
          {/if}
        </div>
      {:else if showClose}
        <button
          type="button"
          class="close-button"
          aria-label="Close warning"
          on:click="{() => (visible = false)}"
        >
          <Close size="{24}" />
        </button>
      {/if}
    </div>
  {/if}
{:else}
  <section
    class:bx-info-block="{true}"
    class:bx--info-block--light="{light}"
    aria-labelledby="{heading ? 'info-block-title' : undefined}"
  >
    {#if heading}
      <svelte:element
        this="{`h${safeLevel}`}"
        id="info-block-title"
        class:bx-info-block-title="{true}"
      >
        {heading}
      </svelte:element>
    {/if}

    {#if description}
      <p class:bx-info-block-description="{true}">
        {description}
      </p>
    {/if}
  </section>
{/if}

<style>
  /* ── InfoBlock styles ── */
  .bx-info-block {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
    background-color: var(--pax-white);
    border: 1px solid var(--pax-black);
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
  }

  .bx-info-block .bx-info-block-title {
    margin: 0;
    color: var(--pax-black);
  }

  .bx-info-block .bx-info-block-description {
    color: #6f7681;
    font-size: 0.85rem;
    line-height: 1.1;
    margin: 0;
  }

  .bx--info-block--light {
    background-color: var(--pax-gray-100);
    border-color: #d9d9d9;
  }

  .bx--info-block--light .bx-info-block-title {
    color: #6f7681;
    font-size: 0.85rem !important;
    font-weight: normal;
  }

  .bx--info-block--light .bx-info-block-description {
    color: var(--pax-black);
    font-size: 1.05rem !important;
    font-weight: bold;
  }

  /* ── InlineNotice styles ── */
  .warning-notice {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    /* Apps may set flex-shrink: 0 globally, the notice must still shrink in flex rows and let the message wrap */
    flex-shrink: 1;
    box-sizing: border-box;
    padding: 0.75rem 1rem;
    background-color: var(--pax-red);
    border: 0;
    border-radius: 0.5rem;
    color: var(--pax-black);
  }

  .warning-notice.fit {
    width: fit-content;
    max-width: 100%;
  }

  .warning-notice.hint {
    background-color: var(--pax-green);
  }

  .warning-notice.info {
    background-color: var(--pax-blue);
  }

  .warning-notice.neutral {
    background-color: #fafafa;
    border: 1px solid var(--border-color);
    color: var(--pax-black);
  }

  .warning-notice.pax-ai {
    min-height: 4.5rem;
    box-sizing: border-box;
    background: var(--pax-orange);
    border-color: transparent;
    color: var(--pax-white);
  }

  .warning-notice.loading {
    padding-right: 1rem;
  }

  .warning-notice.loading .notice-actions {
    display: none;
  }

  .warning-notice.pax-ai .attention-icon {
    align-self: stretch;
    width: 3rem;
    height: 3rem;
    border-radius: 0.5rem;
    filter: none;
  }

  .warning-notice.pax-ai .close-button {
    color: var(--pax-white);
  }

  .warning-notice.loading .notice-progress {
    background: rgba(255, 255, 255, 0.28);
  }

  .warning-notice.loading .notice-progress-value {
    background: rgba(255, 255, 255, 0.72);
  }

  .warning-notice.seamless {
    height: var(--app-header-height);
    border: 0;
    border-bottom: 1px solid var(--border-color);
    border-radius: 0;
  }

  .warning-notice.hint .attention-icon {
    color: var(--pax-black);
  }

  .warning-content {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.25rem;
  }

  .attention-icon {
    display: flex;
    flex: 0 0 auto;
    align-self: center;
    width: 1.5rem;
    height: 1.5rem;
    align-items: center;
    justify-content: center;
  }

  .attention-icon :global(img),
  .attention-icon :global(svg) {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .warning-title {
    margin: 0;
  }

  .warning-message {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.1;
    /* Wrap between words, so the narrowest notice is its longest word, icon, title or buttons */
    overflow-wrap: break-word;
  }

  /* Slotted message content keeps the notice type, app-wide paragraph styles must not change it */
  .warning-message :global(p),
  .warning-message :global(span),
  .warning-message :global(li) {
    font-size: inherit;
    line-height: inherit;
  }

  .notice-progress {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 3rem;
    min-height: 3rem;
    flex: 0 0 3rem;
    overflow: hidden;
    background: var(--pax-smoke);
    border-radius: 0.5rem;
  }

  .notice-progress-value {
    position: absolute;
    inset: 0 auto 0 0;
    height: 100%;
    overflow: hidden;
    background: linear-gradient(
      90deg,
      var(--pax-orange),
      #ff7f52 50%,
      var(--pax-orange)
    );
    background-size: 200% 100%;
    animation: notice-progress-gradient 2.4s ease-in-out infinite;
    transition: width 180ms ease-in-out;
  }

  .notice-progress-label {
    position: relative;
    z-index: 1;
    width: 100%;
    padding: 0 0.75rem;
    box-sizing: border-box;
    color: var(--pax-black);
    font-size: 0.85rem;
    font-weight: 400;
    text-align: center;
  }

  .warning-notice.loading .notice-progress-label {
    color: var(--pax-black);
  }

  @keyframes notice-progress-gradient {
    from {
      background-position: 0 0;
    }
    to {
      background-position: 100% 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .notice-progress-value {
      animation: none;
    }
  }

  .close-button {
    display: grid;
    flex: 0 0 auto;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    place-items: center;
    align-self: center;
    background: transparent;
    border: 0;
    cursor: pointer;
    color: currentColor;
  }

  .notice-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.5rem;
  }

  .notice-cancel {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
  }
</style>
